import { apiService } from './apiService';
import type { SissiBand, SissiRolling7 } from './sissiScoreService';
import { API_BASE_URL } from '../config/env';

export interface Patient {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  phone_number: string;
  username: string;
  date_of_birth: string;
  gender: string;
  address: string;
  avatarUrl: string | null;
  rmrKcalDay: number;
  weight: number;
  // Altura del perfil, en cm: no es una medida corporal (no tiene histórico),
  // solo se combina con el último peso para el IMC.
  heightCm?: number | null;
  termsAndConditionsAccepted: string | null;
  lastActivityAt: string | null;
  /** IANA zone the patient set in the app (e.g. "Europe/Madrid"). Read-only here. */
  timezone?: string | null;
  app_version: string | null;
  // Sistema operativo del móvil del paciente. Sólo lectura: la app lo
  // reescribe en cada actualización de perfil, y el backend ignora ambos
  // campos en PUT/PATCH desde la clínica. El backend los normaliza a
  // minúsculas, así que basta comparar con 'android' / 'ios'.
  app_os: 'android' | 'ios' | null;
  app_os_version: string | null;
  fcm_token: string | null;
  weeklyDigestEnabled?: boolean;
  insightsEnabled?: boolean;
  serviceStatus?: ServiceStatus;
  /** Important insight findings from the last 14 days. Counted by the endpoint, not by the client. */
  openInsights?: number;
  /** Why this patient needs a decision, or null when they do not. Computed by
   *  the endpoint with the same expression that orders the list, so no screen
   *  has to work it out again and disagree. */
  attentionReason?: 'never' | 'offPlan' | 'inactive' | 'watch' | 'labReview' | null;
  /** Blood analyses waiting for the clinic's validation, counted by the endpoint. */
  pendingLabPanels?: number;
  /** When the oldest of those entered the queue, or null. */
  oldestPendingLabPanelAt?: string | null;
  /** Scores of the last 7 days with data, oldest first. A day the patient did
   *  not take part in is absent rather than zero, same as the rolling mean. */
  sissiSparkline?: { date: string; score: number }[];
  assignedProfessional?: AssignedProfessional | null;
  clinic?: Clinic | null;
  // Último día CON score del paciente, que no tiene por qué ser hoy. null sólo
  // si nunca ha tenido ninguno.
  sissiScore?: PatientSissiScore | null;
  /** Summary of the onboarding health questionnaire. Absent on older backends. */
  healthQuestionnaire?: PatientHealthQuestionnaire | null;
}

/** Status of the chat-driven health questionnaire, computed by the server. The
 *  answers themselves come from healthQuestionnaireService. */
export interface PatientHealthQuestionnaire {
  /** Set by the backend when the clinic asks again; not edited from the clinic. */
  required: boolean;
  status: 'not_started' | 'in_progress' | 'completed';
  progressPct: number | null;
  startedAt: string | null;
  lastAnsweredAt: string | null;
  completedAt: string | null;
  redRules: number;
  urgent: boolean;
  definitionVersion: string;
  hasPlan: boolean;
  /** False when the questionnaire is switched off for the clinic. Absent on older backends. */
  available?: boolean;
}

// Resumen que el listado anida por paciente (una subconsulta, sin N+1). La
// banda la calcula el servidor: el cliente no vuelve a aplicar umbrales.
//
// El servidor devuelve la fila más reciente CON score, no la más reciente a
// secas: el cron escribe la fila de hoy para todo el mundo a las 02:30, sin
// score hasta que el paciente registra algo, así que ordenar sólo por fecha
// dejaría el listado entero en blanco cada mañana. Por eso `date` puede ser de
// hace días y el badge lo enseña siempre al pasar el ratón.
export interface PatientSissiScore {
  score: number | null; // 0–100 — el día suelto
  band: SissiBand | null; // null cuando no hay score
  // Lo que pinta el anillo. El valor diario no sirve para escanear una cohorte:
  // cae a 0 el día que el paciente no registra, así que la columna parpadearía.
  // Llega con una consulta extra por página, no por paciente.
  rolling7: SissiRolling7 | null;
  date: string; // YYYY-MM-DD — el día del score, no necesariamente hoy
  dataConfidence: number | null; // 0–1
  isProvisional: boolean;
  isCalibrating: boolean;
}

export interface AssignedProfessional {
  id: number;
  name: string;
  email: string;
  role: string;
}

export interface Clinic {
  id: number;
  name: string;
}

export interface ServiceStatus {
  pushNotifications: boolean;
  agentEnabled: boolean;
  agentProactiveEnabled: boolean;
  sleepEnabled: boolean;
  insightsEnabled: boolean;
  // The patient's WhatsApp link (they connect it from the app). Absent on
  // older backends.
  whatsappStatus?: 'none' | 'pending_consent' | 'active' | 'revoked';
}

export interface CreatePatientData {
  email: string;
  firstName: string;
  lastName: string;
  gender: 'male' | 'female' | 'other';
  dateOfBirth: string;
  rmrKcalDay: number;
  weight: number;
  phoneNumber?: string;
  password?: string;
  welcomeEmailType?: 'welcome' | 'welcomeWithPassword' | 'welcomeMonarka' | 'welcomeMonarkaWithPassword';
  /** Only admins send these; for a doctor the backend uses their own clinic and assigns them. */
  clinicId?: number;
  assignedProfessionalId?: number;
}

export interface ClinicProfessionalOption {
  id: number;
  name: string;
  role: 'doctor' | 'nutritionist';
}

export interface ClinicAssignmentOption {
  id: number;
  name: string;
  professionals: ClinicProfessionalOption[];
}

export interface UpdatePatientData {
  email: string;
  firstName: string;
  lastName: string;
  gender: 'male' | 'female' | 'other';
  dateOfBirth: string;
  rmrKcalDay: number;
  weight: number;
  phone_number: string;
  address: string;
  weeklyDigestEnabled: boolean;
  insightsEnabled: boolean;
}

export interface PaginatedResponse {
  actualPage: number;
  count: number;
  perpage: number;
  results: Patient[];
  totalPages: number;
}

export type PatientActivityStatus = 'active' | 'inactive' | 'never';

export const getPatientActivityStatus = (patient: Patient): PatientActivityStatus => {
  if (!patient.lastActivityAt) return 'never';
  const lastActivity = new Date(patient.lastActivityAt);
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return lastActivity < oneWeekAgo ? 'inactive' : 'active';
};

export type PatientOrdering =
  | 'attention'
  | 'recent'
  | 'name'
  | '-name'
  | 'activity'
  | '-activity'
  | 'sissi'
  | '-sissi'
  | 'insights';

export interface PatientListQuery {
  page?: number;
  search?: string;
  ordering?: PatientOrdering;
  /** Sissi bands to keep. Empty means every band. */
  bands?: SissiBand[];
  /** Activity buckets to keep. Empty means every bucket. */
  activity?: PatientActivityStatus[];
  /** Only patients with at least one important insight from the last 14 days. */
  hasInsights?: boolean;
  /** Only patients the endpoint flagged as needing attention. */
  needsAttention?: boolean;
  perPage?: number;
  /** Admin only: patients of one clinic. A doctor already sees only their own. */
  clinicId?: number;
}

export const patientsService = {
  getPatients: async (query: PatientListQuery = {}): Promise<PaginatedResponse> => {
    const params = new URLSearchParams({
      is_active: 'true',
      page: String(query.page ?? 1),
    });
    if (query.search?.trim()) params.set('search', query.search.trim());
    if (query.ordering) params.set('ordering', query.ordering);
    if (query.bands?.length) params.set('band', query.bands.join(','));
    if (query.activity?.length) params.set('activity', query.activity.join(','));
    if (query.hasInsights) params.set('has_insights', '1');
    if (query.needsAttention) params.set('needs_attention', '1');
    if (query.perPage) params.set('per_page', String(query.perPage));
    if (query.clinicId) params.set('clinic_id', String(query.clinicId));

    try {
      return await apiService.get(`${API_BASE_URL}/clinic/patient?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching patients:', error);
      throw error;
    }
  },

  getPatientById: async (id: string | number): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.get(url);
      return response;
    } catch (error) {
      console.error(`Error fetching patient with id ${id}:`, error);
      throw error;
    }
  },

  createPatient: async (data: CreatePatientData): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient`;
    try {
      const payload: any = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        gender: data.gender,
        dateOfBirth: data.dateOfBirth,
        rmrKcalDay: data.rmrKcalDay,
        weight: data.weight,
      };
      
      if (data.phoneNumber) {
        payload.phone_number = data.phoneNumber;
      }
      
      if (data.password) {
        payload.password = data.password;
      }
      
      if (data.welcomeEmailType) {
        payload.welcomeEmailType = data.welcomeEmailType;
      }
      
      if (data.clinicId !== undefined) {
        payload.clinicId = data.clinicId;
      }

      if (data.assignedProfessionalId !== undefined) {
        payload.assignedProfessionalId = data.assignedProfessionalId;
      }

      const response = await apiService.post(url, payload);
      return response;
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  },

  /** Admin only: active clinics with their professionals, for the create-patient form. */
  getClinicAssignmentOptions: async (): Promise<ClinicAssignmentOption[]> => {
    try {
      return await apiService.get(`${API_BASE_URL}/clinic/clinics/`);
    } catch (error) {
      console.error('Error fetching clinics:', error);
      throw error;
    }
  },

  updatePatient: async (id: string | number, data: Partial<UpdatePatientData>): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.put(url, data);
      return response;
    } catch (error) {
      console.error(`Error updating patient with id ${id}:`, error);
      throw error;
    }
  },

  // Partial update via PATCH — used for standalone field toggles (e.g.
  // insightsEnabled, weeklyDigestEnabled) without sending the full patient form.
  patchPatient: async (id: string | number, data: Partial<UpdatePatientData>): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.patch(url, data);
      return response;
    } catch (error) {
      console.error(`Error patching patient with id ${id}:`, error);
      throw error;
    }
  },

  updatePatientPassword: async (id: string | number, password: string): Promise<Patient> => {
    const url = `${API_BASE_URL}/clinic/patient/${id}/`;
    try {
      const response = await apiService.put(url, { password });
      return response;
    } catch (error) {
      console.error(`Error updating password for patient with id ${id}:`, error);
      throw error;
    }
  },

  sendWelcomeEmail: async (userId: number, welcomeEmailType: 'welcome' | 'welcomeWithPassword' | 'welcomeMonarka' | 'welcomeMonarkaWithPassword' | 'accessReminder'): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/patient/${userId}/welcome-email/`;
    try {
      await apiService.post(url, { welcomeEmailType });
    } catch (error) {
      console.error(`Error sending welcome email to user ${userId}:`, error);
      throw error;
    }
  },

  /** The same endpoint the other way round. Deactivation is reversible, which
   *  is what lets the confirmation promise it and the toast offer an undo. */
  reactivatePatient: async (userId: number): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/patient/${userId}/activation/`;
    try {
      await apiService.patch(url, { is_active: true });
    } catch (error) {
      console.error('Error reactivating patient:', error);
      throw error;
    }
  },

  deactivatePatient: async (userId: number): Promise<void> => {
    const url = `${API_BASE_URL}/clinic/patient/${userId}/activation/`;
    try {
      await apiService.patch(url, { is_active: false });
    } catch (error) {
      console.error(`Error deactivating patient with userId ${userId}:`, error);
      throw error;
    }
  },
}; 